import React, { useState } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ArrowLeft, CheckCircle, AlertCircle } from 'lucide-react'
import { http } from '@/services/api'
import { DiamondIcon, GoldText, LuxuryButton } from '@/components/UI'

export const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setIsLoading(true)

    try {
      await http('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email }),
      })
      setSuccess(true)
    } catch (err: unknown) {
      const error = err as { message?: string }
      setError(error?.message || 'Failed to send reset email. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-12" style={{ background: 'var(--bg-canvas)' }}>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        <div
          className="rounded-2xl p-8"
          style={{
            background: 'var(--bg-card)',
            border: '1px solid var(--border)',
            boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
          }}
        >
          {/* Back to login link */}
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-sm text-[var(--accent)] hover:underline mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to login
          </Link>

          {/* Header */}
          <div className="text-center mb-8">
            {/* Diamond Badge */}
            <div
              className="flex items-center justify-center gap-4 mb-6"
              style={{
                color: 'var(--accent)',
                textTransform: 'uppercase',
                letterSpacing: 'var(--tracking-label)',
                fontSize: '0.84rem',
                fontWeight: 600,
              }}
            >
              <DiamondIcon size="md" glow />
              <span>Account Recovery</span>
              <DiamondIcon size="md" glow />
            </div>
            <h2
              style={{
                fontFamily: 'var(--font-family-display)',
                fontSize: 'var(--font-size-display-sm)',
                fontWeight: 700,
                lineHeight: 1.12,
                letterSpacing: 'var(--tracking-tight)',
                marginBottom: '0.5rem',
                color: 'var(--text-primary)',
              }}
            >
              Reset your{' '}
              <GoldText as="span" glow>
                Password
              </GoldText>
            </h2>
            <p className="text-[var(--text-muted)]" style={{ fontSize: '1rem', lineHeight: 1.6, fontWeight: 400 }}>
              Enter your email address and we'll send you a link to reset your password.
            </p>
          </div>

          {/* Success message */}
          {success ? (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              className="text-center py-8"
            >
              <div className="flex justify-center mb-4">
                <div
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{
                    background: 'rgba(16, 185, 129, 0.1)',
                    border: '2px solid rgba(16, 185, 129, 0.3)',
                  }}
                >
                  <CheckCircle className="w-8 h-8 text-green-400" />
                </div>
              </div>
              <h3 className="text-lg font-semibold text-[var(--text-primary)] mb-2">
                Check your email
              </h3>
              <p className="text-[var(--text-secondary)] mb-6">
                If an account exists with <strong>{email}</strong>, you'll receive a password reset link shortly.
              </p>
              <p className="text-sm text-[var(--text-muted)]">
                Didn't receive the email? Check your spam folder or{' '}
                <button
                  onClick={() => {
                    setSuccess(false)
                    setEmail('')
                  }}
                  className="text-[var(--accent)] hover:underline"
                >
                  try again
                </button>
              </p>
            </motion.div>
          ) : (
            <>
              {/* Error message */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="mb-6 p-4 rounded-lg flex items-center gap-3"
                  style={{
                    background: 'color-mix(in srgb, var(--state-danger) 10%, transparent)',
                    border: '1px solid color-mix(in srgb, var(--state-danger) 30%, transparent)',
                  }}
                >
                  <AlertCircle className="w-5 h-5 text-[var(--state-danger)] flex-shrink-0" />
                  <p className="text-sm text-[var(--state-danger)]">{error}</p>
                </motion.div>
              )}

              {/* Form */}
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
                  <label className="block text-sm font-medium text-[var(--text-primary)] mb-2">
                    Email address
                  </label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="w-full px-4 py-3 rounded-lg bg-[var(--bg-input)] border border-[var(--border)] text-[var(--text-primary)] focus:outline-none focus:ring-2 focus:ring-[var(--accent)]"
                    placeholder="you@company.com"
                    required
                    disabled={isLoading}
                  />
                </div>

                <LuxuryButton
                  type="submit"
                  disabled={isLoading}
                  fullWidth
                  size="md"
                >
                  {isLoading ? 'Sending...' : 'Send Reset Link'}
                </LuxuryButton>
              </form>

              {/* Help text */}
              <p className="mt-6 text-center text-sm text-[var(--text-muted)]">
                Remember your password?{' '}
                <Link to="/login" className="text-[var(--accent)] hover:underline font-medium">
                  Sign in
                </Link>
              </p>
            </>
          )}
        </div>
      </motion.div>
    </div>
  )
}

export default ForgotPasswordPage
